import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Share2, 
  Plus, 
  Edit3, 
  Trash2, 
  User, 
  ChevronDown,
  ChevronUp,
  Info,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import { MAP_TYPES } from '../store/mockData';
import { toast } from 'sonner';

const SHARE_TRIGGERS = [
  { value: 'continuity', label: 'Only when continuity is triggered' },
  { value: 'manual', label: 'Only via manual export' },
  { value: 'both', label: 'Both continuity trigger and manual export' },
];

const Sharing = () => {
  const { 
    getContactById, 
    contacts,
    shareSettings, 
    fetchSharing, 
    fetchContacts,
    addShareSetting,
    updateShareSetting,
    deleteShareSetting,
    isAuthenticated, 
    loading 
  } = useLifely();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([
            fetchSharing(),
            fetchContacts()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load sharing data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchSharing, fetchContacts]);
  
  // Check if user came from continuity setup
  const fromContinuity = location.state?.from === 'continuity' || 
                         document.referrer.includes('/continuity');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingShare, setEditingShare] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  const [selectedContactId, setSelectedContactId] = useState('');
  const [shareType, setShareType] = useState('all_maps');
  const [selectedMaps, setSelectedMaps] = useState([]);
  const [includeWillStatus, setIncludeWillStatus] = useState(false);
  const [includeContacts, setIncludeContacts] = useState(false);
  const [shareTrigger, setShareTrigger] = useState('continuity');
  const [shareNotes, setShareNotes] = useState('');

  const resetForm = () => {
    setSelectedContactId('');
    setShareType('all_maps');
    setSelectedMaps([]);
    setIncludeWillStatus(false);
    setIncludeContacts(false);
    setShareTrigger('continuity');
    setShareNotes('');
    setEditingShare(null);
  };

  const handleOpenDialog = (share = null) => {
    if (share) {
      setEditingShare(share);
      setSelectedContactId(share.contactId);
      setShareType(share.shareType);
      setSelectedMaps(share.selectedMaps || []);
      setIncludeWillStatus(share.includeWillStatus);
      setIncludeContacts(share.includeContacts);
      setShareTrigger(share.shareTrigger);
      setShareNotes(share.notes || '');
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedContactId) {
      toast.error('Please select a contact');
      return;
    }
    
    const shareData = {
      contactId: selectedContactId,
      shareType,
      selectedMaps: shareType === 'specific_maps' ? selectedMaps : [],
      includeWillStatus,
      includeContacts,
      shareTrigger,
      notes: shareNotes,
    };
    
    try {
      if (editingShare) {
        await updateShareSetting(editingShare.id, shareData);
        // Refresh from server to get correct data
        await fetchSharing();
        toast.success('Sharing settings updated');
      } else {
        await addShareSetting(shareData);
        toast.success('Sharing settings added');
      }
      setIsDialogOpen(false);
      resetForm();
      
      // Navigate back to continuity if that's where we came from
      if (fromContinuity) {
        navigate('/continuity/setup');
      }
    } catch (err) {
      toast.error('Failed to save sharing settings');
      console.error('Save error:', err);
    }
  };

  const handleDelete = async (shareId) => {
    try {
      await deleteShareSetting(shareId);
      toast.success('Sharing settings removed');
    } catch (err) {
      toast.error('Failed to remove sharing settings');
      console.error('Delete error:', err);
    }
  };

  const toggleMap = (mapId) => {
    setSelectedMaps(prev => 
      prev.includes(mapId) ? prev.filter(id => id !== mapId) : [...prev, mapId]
    );
  };

  const getShareSummary = (share) => {
    const parts = [];
    if (share.shareType === 'all_maps') {
      parts.push('All Maps');
    } else if (share.selectedMaps?.length > 0) {
      const mapNames = share.selectedMaps.map(id => MAP_TYPES.find(m => m.id === id)?.name || id);
      parts.push(mapNames.length <= 2 ? mapNames.join(', ') : `${mapNames.length} Maps`);
    }
    if (share.includeWillStatus) parts.push('Will Status');
    if (share.includeContacts) parts.push('Contacts');
    return parts.join(' + ') || 'No content selected';
  };

  // Show loading state
  if (loading.sharing && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sharing-page">
      {/* Return to Continuity Banner */}
      {fromContinuity && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            Add sharing rules to specify who should be notified when continuity triggers.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/continuity/setup')}
            data-testid="return-to-continuity-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Continuity
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-heading font-semibold text-foreground">Sharing Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure who can access your information and when</p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Nothing is shared automatically</p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Your information is only shared when triggered by your continuity rules or when you manually send it via Export.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Continuity escalation:</strong> If your check-ins are missed and 48 hours pass without you responding, 
              each nominee below will receive a secure, one-time link containing <strong>only the maps and data you select here</strong>, nothing more.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Sharing Rules ({shareSettings.length})</h2>
          <Button onClick={() => handleOpenDialog()} data-testid="add-sharing-btn">
            <Plus className="w-4 h-4 mr-2" />Add Sharing Rule
          </Button>
        </div>

        {shareSettings.length === 0 ? (
          <Card className="border border-dashed border-border bg-muted/30">
            <CardContent className="py-12 text-center">
              <Share2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No sharing rules configured</p>
              <p className="text-sm text-muted-foreground mt-1">Add a sharing rule to specify who should receive your information.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {shareSettings.map((share) => {
              // Use the contactName directly from state (transformed from backend)
              const displayName = share.contactName || 'No contact selected';
              
              return (
              <Card key={share.id} className="border border-border">
                <div 
                  className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-muted/30"
                  onClick={() => setExpandedId(expandedId === share.id ? null : share.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {displayName}
                      </p>
                      <p className="text-sm text-muted-foreground">{getShareSummary(share)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{share.shareTrigger}</Badge>
                    {expandedId === share.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {expandedId === share.id && (
                  <CardContent className="pt-0 pb-4 border-t">
                    <div className="pt-4 flex gap-2">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDialog(share); }}>
                        <Edit3 className="w-3.5 h-3.5 mr-1" />Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(share.id); }}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingShare ? 'Edit Sharing Rule' : 'Add Sharing Rule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Who should receive this information?</Label>
              <ContactSelector value={selectedContactId} onSelect={setSelectedContactId} placeholder="Select a contact..." />
            </div>

            <div className="space-y-3">
              <Label>What should they receive?</Label>
              <RadioGroup value={shareType} onValueChange={setShareType}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all_maps" id="all-maps" />
                  <Label htmlFor="all-maps" className="font-normal">All maps</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="specific_maps" id="specific-maps" />
                  <Label htmlFor="specific-maps" className="font-normal">Specific maps only</Label>
                </div>
              </RadioGroup>
              {shareType === 'specific_maps' && (
                <div className="pl-6 grid grid-cols-2 gap-2">
                  {MAP_TYPES.map(map => (
                    <div key={map.id} className="flex items-center space-x-2">
                      <Checkbox id={`map-${map.id}`} checked={selectedMaps.includes(map.id)} onCheckedChange={() => toggleMap(map.id)} />
                      <Label htmlFor={`map-${map.id}`} className="text-sm font-normal">{map.name}</Label>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="include-will" checked={includeWillStatus} onCheckedChange={setIncludeWillStatus} />
                  <Label htmlFor="include-will" className="font-normal">Include Will Status</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="include-contacts" checked={includeContacts} onCheckedChange={setIncludeContacts} />
                  <Label htmlFor="include-contacts" className="font-normal">Include Contacts list</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>When should they receive it?</Label>
              <Select value={shareTrigger} onValueChange={setShareTrigger}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHARE_TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note to recipient (optional)</Label>
              <Textarea value={shareNotes} onChange={(e) => setShareNotes(e.target.value)} placeholder="Any message..." className="min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingShare ? 'Update' : 'Add'} Sharing Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sharing;
